import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction'; // for dateClick, eventClick, etc.
// import listPlugin from '@fullcalendar/list'; // for list view - temporarily commented out
import { LoadingSpinner } from '../common/LoadingSpinner'; // Import LoadingSpinner
import { Modal } from '../common/Modal'; // Import Modal
import { EventForm } from './EventForm'; // Import EventForm
import { calendarService } from '../../services/calendar.service'; // Import calendar service
import type { CalendarEvent } from '../../types/calendar'; // Import CalendarEvent type

const useWindowSize = () => {
  const [size, setSize] = useState([0, 0]);
  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
};

export const PastoralCalendar: React.FC = () => {
  const [width] = useWindowSize();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const fetchedEvents = await calendarService.getAllEvents();
      setEvents(fetchedEvents.map(e => ({
        ...e,
        start: e.start,
        end: e.end || undefined,
      })));
    } catch (error) {
      console.error("Error fetching calendar events:", error);
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleDateClick = (arg: { dateStr: string; allDay: boolean }) => {
    setSelectedDate(new Date(arg.dateStr));
    setSelectedEvent(null);
    setIsEventModalOpen(true);
  };

  const handleEventClick = (arg: { event: any }) => {
    const clickedEvent = events.find(e => e.id === arg.event.id);
    if (clickedEvent) {
      setSelectedEvent(clickedEvent);
      setSelectedDate(null);
      setIsEventModalOpen(true);
    }
  };
  
  const handleEventModalSuccess = () => {
    setIsEventModalOpen(false);
    fetchEvents();
  };

  const handleEventModalCancel = () => {
    setIsEventModalOpen(false);
  };

  if (loadingEvents) {
    return <LoadingSpinner />;
  }

  const isMobile = width < 768;

  return (
    <div className="h-[38vh] text-sm">
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]} // listPlugin temporarily removed
        initialView="dayGridMonth"
        headerToolbar={isMobile ? {
          left: 'prev,next',
          center: 'title',
          right: 'today'
        } : {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay' // listWeek temporarily removed
        }}
        footerToolbar={isMobile ? {
          center: 'dayGridMonth,timeGridWeek,timeGridDay' // listWeek temporarily removed
        } : undefined}
        events={events}
        editable={true}
        selectable={true}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        locale="ko"
        buttonText={{
          today:    '오늘',
          month:    '월',
          week:     '주',
          day:      '일',
          // list:     '목록' // temporarily commented out
        }}
        height="100%"
      />
      {isEventModalOpen && (
        <Modal isOpen={isEventModalOpen} onClose={handleEventModalCancel} title={selectedEvent ? "이벤트 수정" : "새 이벤트 추가"}>
          <EventForm 
            eventData={selectedEvent} 
            selectedDate={selectedDate} 
            onSuccess={handleEventModalSuccess} 
            onCancel={handleEventModalCancel} 
          />
        </Modal>
      )}
    </div>
  );
};
